import _ from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useSearch, useViewer, useViewerRoles } from '@treams/treams-hooks';
import {
	ActivityIcon,
	GearIcon,
	GridIcon,
	HomeIcon,
	SearchIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Input, Label, Lazy, Paragraph, Sidebar, Skeleton } from '@treams/treams-ui';

const GroupTitle = styled(Sidebar.SectionTitle)`
	padding-left: 0;
`;

function Team(props: {
	group: graphql.Group;
	users?: { id: string; firstName: string; lastName: string; groupID: string }[];
	query: string;
}) {
	const { t } = useTranslation();

	const users = props.users?.filter((user) => user.groupID === props.group.id);

	if (users && users.length === 0) {
		return null;
	}

	return (
		<Sidebar.Section title={<GroupTitle mb={1}>{props.group.name}</GroupTitle>}>
			{users ? (
				users.length === 0 ? (
					<Label textAlign="center" mt={2}>
						{t('sidebar.manager.group.empty')}
					</Label>
				) : (
					_.chunk(users, 10).map((page, i) => (
						<Lazy key={i}>
							{page.map((user) => (
								<Sidebar.User
									key={user.id}
									to={`/users/${user.id}`}
									user={user}
									query={props.query}
								/>
							))}
						</Lazy>
					))
				)
			) : (
				<Skeleton.Repeat count={3}>
					<Sidebar.User.Skeleton />
				</Skeleton.Repeat>
			)}
		</Sidebar.Section>
	);
}

function TeamSkeleton() {
	return (
		<Box pt={3}>
			<GroupTitle mb={1}>
				<Paragraph.Skeleton />
			</GroupTitle>
		</Box>
	);
}

function TeamsOverview() {
	const { t } = useTranslation();

	const viewer = useViewer();
	const roles = graphql.useViewerGroupsQuery();
	const [query, setQuery] = useState('');
	const now = useRef(Date.now()).current;

	const filters = [
		'isArchived:false',
		`activatedAt <= ${now}`,
		'(' + ['deactivatedAt = 0', `deactivatedAt > ${now}`].join(' OR ') + ')',
	].join(' AND ');

	const { results, loading } = useSearch(query, { filters, excludeViewer: true });

	const managerRoleTypes = new Set([
		graphql.RoleType.PrimaryManager,
		graphql.RoleType.CollaborativeManager,
		graphql.RoleType.Coach,
		graphql.RoleType.Owner,
	]);

	const groups = _.uniqBy(
		_.sortBy(
			roles.data?.viewer.roles.flatMap((role) => {
				if (role.targetV2?.__typename === 'Group' && managerRoleTypes.has(role.role.type)) {
					return role.targetV2 as graphql.Group;
				} else {
					return [];
				}
			}) ?? [],
			'name',
		),
		'id',
	);

	const people = _.uniq(
		roles.data?.viewer.roles.flatMap((role) => {
			if (role.targetV2?.__typename === 'User' && role.targetV2.id !== viewer?.id) {
				return role.targetV2.id;
			} else {
				return [];
			}
		}) ?? [],
	);

	const sortedResults = _.sortBy(results, (result) =>
		_.deburr(`${result.firstName} ${result.lastName}`),
	);

	const sortedPeople = sortedResults.filter(({ id }) => people.includes(id));

	return (
		<Box>
			<Input
				search
				icon={<SearchIcon />}
				placeholder={t('sidebar.manager.search.placeholder')}
				loading={loading}
				value={query ? query : undefined}
				onChange={setQuery}
			/>
			{roles.data ? (
				<>
					{sortedPeople.length > 0 && (
						<Sidebar.Section
							title={<GroupTitle mb={1}>{t('sidebar.manager.group.oneOnOne')}</GroupTitle>}
						>
							{sortedPeople.map((person) => (
								<Sidebar.User
									key={person.id}
									to={`/users/${person.id}`}
									user={person}
									query={query}
								/>
							))}
						</Sidebar.Section>
					)}
					{groups?.map((group) => (
						<Team key={group.id} group={group} users={sortedResults} query={query} />
					))}
				</>
			) : (
				<Skeleton.Repeat count={3}>
					<TeamSkeleton />
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

export default function ManagerNav() {
	const { t } = useTranslation();
	const {
		isWorkspaceSuperuser,
		isGroupManager,
		isAdministrator,
		isEngagementOfficer,
		isHRBusinessPartner,
		isPrimaryGroupManager,
	} = useViewerRoles();

	const hasTeamGoals = useFeature(graphql.FeatureName.TeamGoals);
	const hasTalentReviews = useFeature(graphql.FeatureName.TalentReviews);
	const hasTalentReviewsV2 = useFeature(graphql.FeatureName.TalentReviewsV2);
	const hasSearchOnSkills = useFeature(graphql.FeatureName.SearchOnSkills);

	return (
		<>
			<Sidebar.Item icon={<HomeIcon />} title={t('sidebar.startpage')} path={['/manager']} small />
			{hasTeamGoals && (
				<Sidebar.Item
					icon={<TargetIcon />}
					title={t('sidebar.collectiveGoals')}
					path="/goals/collective"
					small
				/>
			)}
			{hasTalentReviews && (isWorkspaceSuperuser || isGroupManager) && (
				<Sidebar.Item
					icon={<GridIcon />}
					title={t('sidebar.talentReview')}
					path={hasTalentReviewsV2 ? '/talent-reviews/v2' : '/talent-reviews'}
					small
				/>
			)}

			{hasSearchOnSkills && isWorkspaceSuperuser && (
				<Sidebar.Item
					icon={<SearchIcon />}
					title={t('sidebar.competencySearch')}
					path="/competencies/search"
					small
				/>
			)}

			<Sidebar.Separator />
			<TeamsOverview />

			<Box flexGrow={1} flexShrink={1} minHeight={0} display={['none', 'none', 'block']} />

			{(isPrimaryGroupManager || isHRBusinessPartner || isEngagementOfficer) && (
				<Sidebar.Item
					icon={<ActivityIcon />}
					title={t('sidebar.analytics')}
					path="/analytics"
					small
				/>
			)}
			{(isWorkspaceSuperuser || isAdministrator || isEngagementOfficer || isHRBusinessPartner) && (
				<Sidebar.Item icon={<GearIcon />} title={t('sidebar.manage')} path="/settings" />
			)}
		</>
	);
}
